import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Check, X, Store, Users2, ChevronRight, Search, Trash2 } from "lucide-react";

const STATUS_MAP = {
  pending: { label: "In attesa", cls: "bg-[var(--mp-yellow)] text-[var(--mp-ink)]" },
  approved: { label: "Approvato", cls: "bg-[var(--mp-ink)] text-white" },
  rejected: { label: "Rifiutato", cls: "bg-[var(--mp-primary)] text-white" },
};

const STATUS_TABS = [
  { value: "all", label: "Tutti" },
  { value: "pending", label: "In attesa" },
  { value: "approved", label: "Approvati" },
  { value: "rejected", label: "Rifiutati" },
];

export default function AdminUsers() {
  const [stats, setStats] = useState(null);
  const [kind, setKind] = useState("agency"); // "agency" | "client"
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [agency, setAgency] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/agency-requests"),
        api.get("/staff/client-requests"),
      ]);
      setStats(s.data);
      setAgency(a.data.items || []);
      setClients(c.data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const actAgency = async (id, action) => {
    setWorkingId(id);
    try {
      await api.post(`/admin/agency-requests/${id}`, { action });
      toast.success(
        action === "approve" ? "Collaboratore approvato" : "Richiesta rifiutata"
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const actClient = async (id, action) => {
    setWorkingId(id);
    try {
      await api.post(`/staff/client-requests/${id}`, { action });
      toast.success(
        action === "approve" ? "Cliente approvato" : "Richiesta rifiutata"
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const deleteUser = async (u) => {
    const label = u.name_salone || u.full_name || u.email;
    const first = window.confirm(
      `Eliminare definitivamente "${label}"?\n\nVerranno cancellati anche tutti i suoi messaggi, idee storie e post programmati.\n\nQuesta azione NON è reversibile.`
    );
    if (!first) return;
    const typed = window.prompt(
      `Per confermare scrivi: ELIMINA\n\n(stai per cancellare ${label})`
    );
    if ((typed || "").trim().toUpperCase() !== "ELIMINA") {
      toast.info("Eliminazione annullata");
      return;
    }
    setWorkingId(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      const d = data?.deleted || {};
      toast.success(
        `Eliminato "${label}" (msg: ${d.messages || 0}, idee: ${
          d.story_ideas || 0
        }, post: ${d.scheduled_posts || 0})`
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const list = kind === "agency" ? agency : clients;
  const byStatus =
    statusFilter === "all"
      ? list
      : list.filter((u) => u.status === statusFilter);
  const needle = search.trim().toLowerCase();
  const filtered = !needle
    ? byStatus
    : byStatus.filter((u) => {
        const hay = [
          u.email,
          u.full_name,
          u.name_salone,
          u.referente_nome,
          u.referente_cognome,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });

  return (
    <div data-testid="admin-users-page" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Amministrazione
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Utenti della piattaforma
        </h1>
      </div>

      {/* KPI cards — clickable filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Agenzia · in attesa"
          value={stats?.pending_agency ?? "—"}
          tone="yellow"
          active={kind === "agency" && statusFilter === "pending"}
          onClick={() => {
            setKind("agency");
            setStatusFilter("pending");
          }}
          testId="kpi-agency-pending"
        />
        <StatCard
          label="Agenzia · approvati"
          value={stats?.approved_agency ?? "—"}
          tone="ink"
          active={kind === "agency" && statusFilter === "approved"}
          onClick={() => {
            setKind("agency");
            setStatusFilter("approved");
          }}
          testId="kpi-agency-approved"
        />
        <StatCard
          label="Clienti registrati"
          value={stats?.clients ?? "—"}
          tone="primary"
          active={kind === "client" && statusFilter === "all"}
          onClick={() => {
            setKind("client");
            setStatusFilter("all");
          }}
          testId="kpi-clients-total"
        />
      </div>

      {/* Kind toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 rounded-full bg-white border border-[var(--mp-border)] p-1 w-fit">
          <KindBtn
            active={kind === "agency"}
            onClick={() => setKind("agency")}
            testId="users-kind-agency"
            icon={<Users2 className="h-3.5 w-3.5" />}
            label="Agenzia"
            count={agency.length}
          />
          <KindBtn
            active={kind === "client"}
            onClick={() => setKind("client")}
            testId="users-kind-client"
            icon={<Store className="h-3.5 w-3.5" />}
            label="Clienti"
            count={clients.length}
          />
        </div>

        <div className="flex items-center gap-1 rounded-full bg-white border border-[var(--mp-border)] p-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              data-testid={`users-status-${t.value}`}
              onClick={() => setStatusFilter(t.value)}
              className={`px-3 h-8 rounded-full text-xs font-medium transition-colors ${
                statusFilter === t.value
                  ? "bg-[var(--mp-yellow)] text-[var(--mp-ink)]"
                  : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="rounded-3xl border border-[var(--mp-border)] bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--mp-border)] flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="font-display text-lg">
            {kind === "agency" ? "Account agenzia" : "Account clienti"}
          </div>
          <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mp-muted)]" />
              <Input
                data-testid="users-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  kind === "agency"
                    ? "Cerca per nome o email…"
                    : "Cerca salone, email, referente…"
                }
                className="pl-9 h-9 rounded-full border-[var(--mp-border)]"
              />
            </div>
            <span className="text-xs text-[var(--mp-muted)] whitespace-nowrap">
              {filtered.length}{" "}
              {filtered.length === 1 ? "risultato" : "risultati"}
            </span>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[var(--mp-muted)]">
            Caricamento…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[var(--mp-muted)]">
            {needle
              ? `Nessun risultato per "${search}".`
              : `Nessun ${kind === "agency" ? "account agenzia" : "cliente"} ${
                  statusFilter === "all"
                    ? "registrato."
                    : statusFilter === "pending"
                    ? "in attesa."
                    : statusFilter === "approved"
                    ? "approvato."
                    : "rifiutato."
                }`}
          </div>
        ) : (
          <ul className="divide-y divide-[var(--mp-border)]">
            {filtered.map((u) => {
              const st = STATUS_MAP[u.status] || { label: u.status, cls: "" };
              if (kind === "client") {
                return (
                  <ClientRow
                    key={u.id}
                    u={u}
                    st={st}
                    workingId={workingId}
                    onAct={actClient}
                    onDelete={deleteUser}
                  />
                );
              }
              return (
                <AgencyRow
                  key={u.id}
                  u={u}
                  st={st}
                  workingId={workingId}
                  onAct={actAgency}
                  onDelete={deleteUser}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function AgencyRow({ u, st, workingId, onAct, onDelete }) {
  return (
    <li
      data-testid={`admin-user-row-${u.id}`}
      className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4"
    >
      <div className="flex-1 min-w-0">
        <div className="font-display text-base tracking-tight truncate">
          {u.full_name || u.email}
        </div>
        <div className="text-sm text-[var(--mp-muted)] truncate">{u.email}</div>
      </div>
      <Badge
        className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] w-fit ${st.cls}`}
      >
        {st.label}
      </Badge>
      {u.status === "pending" && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            data-testid={`admin-user-approve-${u.id}`}
            disabled={workingId === u.id}
            onClick={() => onAct(u.id, "approve")}
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Check className="h-4 w-4 mr-1" /> Approva
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={`admin-user-reject-${u.id}`}
            disabled={workingId === u.id}
            onClick={() => onAct(u.id, "reject")}
            className="rounded-full border-[var(--mp-ink)]"
          >
            <X className="h-4 w-4 mr-1" /> Rifiuta
          </Button>
        </div>
      )}
      {u.status === "approved" && (
        <Button
          size="sm"
          variant="outline"
          data-testid={`admin-user-revoke-${u.id}`}
          disabled={workingId === u.id}
          onClick={() => {
            if (
              window.confirm(`Revocare l'accesso a ${u.full_name || u.email}?`)
            )
              onAct(u.id, "reject");
          }}
          className="rounded-full border-[var(--mp-ink)]"
        >
          <X className="h-4 w-4 mr-1" /> Revoca
        </Button>
      )}
      {u.status === "rejected" && (
        <Button
          size="sm"
          data-testid={`admin-user-reapprove-${u.id}`}
          disabled={workingId === u.id}
          onClick={() => onAct(u.id, "approve")}
          className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
        >
          <Check className="h-4 w-4 mr-1" /> Approva
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        data-testid={`admin-user-delete-${u.id}`}
        disabled={workingId === u.id}
        onClick={() => onDelete(u)}
        title="Elimina definitivamente"
        className="rounded-full border-[var(--mp-ink)] text-red-700 hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

function ClientRow({ u, st, workingId, onAct, onDelete }) {
  const salon = u.name_salone || "—";
  const referente = [u.referente_nome, u.referente_cognome]
    .filter(Boolean)
    .join(" ");
  return (
    <li
      data-testid={`admin-client-row-${u.id}`}
      className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4"
    >
      <Link
        to={`/dashboard/clienti/${u.id}`}
        className="flex items-center gap-3 flex-1 min-w-0 group"
        data-testid={`admin-client-open-${u.id}`}
      >
        <div className="h-9 w-9 shrink-0 rounded-full bg-[var(--mp-yellow)] border border-[var(--mp-ink)] grid place-items-center">
          <Store className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-base tracking-tight truncate group-hover:underline">
            {salon}
          </div>
          <div className="text-sm text-[var(--mp-muted)] truncate">
            {u.email}
            {referente ? ` · ${referente}` : ""}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-[var(--mp-muted)] shrink-0 ml-auto hidden sm:block" />
      </Link>
      <Badge
        className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] w-fit ${st.cls}`}
      >
        {st.label}
      </Badge>
      {u.status === "pending" && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            data-testid={`admin-client-approve-${u.id}`}
            disabled={workingId === u.id}
            onClick={() => onAct(u.id, "approve")}
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Check className="h-4 w-4 mr-1" /> Approva
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={`admin-client-reject-${u.id}`}
            disabled={workingId === u.id}
            onClick={() => onAct(u.id, "reject")}
            className="rounded-full border-[var(--mp-ink)]"
          >
            <X className="h-4 w-4 mr-1" /> Rifiuta
          </Button>
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        data-testid={`admin-client-delete-${u.id}`}
        disabled={workingId === u.id}
        onClick={() => onDelete(u)}
        title="Elimina definitivamente"
        className="rounded-full border-[var(--mp-ink)] text-red-700 hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

function KindBtn({ active, onClick, icon, label, count, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 h-8 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
        active
          ? "bg-[var(--mp-ink)] text-white"
          : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
      }`}
    >
      {icon}
      {label}
      <span
        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
          active
            ? "bg-white/20 text-white"
            : "bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function StatCard({ label, value, tone, active, onClick, testId }) {
  const styles = {
    yellow: "bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-[var(--mp-ink)]",
    ink: "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)]",
    primary: "bg-[var(--mp-primary)] text-white border-[var(--mp-ink)]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-3xl border ${styles[tone]} p-6 shadow-brutal-sm transition-transform hover:-translate-y-0.5 ${
        active ? "ring-2 ring-offset-2 ring-[var(--mp-ink)]" : ""
      }`}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.24em] opacity-90">
        {label}
      </div>
      <div className="font-display text-5xl tracking-tighter mt-2">{value}</div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.2em] opacity-80">
        Vedi elenco →
      </div>
    </button>
  );
}
